import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronRight, AlertTriangle, ClipboardList, Building2, Briefcase, FileText, RefreshCw } from "lucide-react";

const CATALOG_ROUTES = {
  centers:                  "centros",
  departments:              "departamentos",
  positions:                "puestos",
  professional_categories:  "categorias-profesionales",
  contribution_groups:      "grupos-cotizacion",
  contract_types:           "tipos-contrato",
  workday_types:            "jornadas",
  employment_statuses:      "situaciones-laborales",
  agreements:               "convenios",
  cost_centers:             "centros-coste",
};

const ICON_MAP = {
  centers: Building2, departments: Briefcase, positions: Briefcase,
  professional_categories: FileText, contribution_groups: FileText,
  contract_types: ClipboardList, workday_types: ClipboardList,
  employment_statuses: ClipboardList, agreements: FileText, cost_centers: FileText,
};

const SEVERITY = {
  high:   { label: "Alto",   class: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "Medio",  class: "bg-amber-100 text-amber-700 border-amber-200" },
  low:    { label: "Bajo",   class: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function CatalogsSummaryPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/hr-catalogs-summary");
      setData(res.data);
    } catch { toast.error("Error al cargar resumen"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="catalogs-summary-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Catálogos RRHH</h1>
          <p className="text-sm text-slate-500 mt-1">Resumen y gestión de catálogos maestros del módulo de personas.</p>
        </div>
        <Button variant="outline" onClick={load} size="sm" className="text-xs rounded-sm" data-testid="reload-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Recargar
        </Button>
      </div>

      {/* Alerts */}
      {!loading && data?.alerts?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30 rounded-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <h2 className="text-xs uppercase font-semibold text-amber-800">{data.alerts_total} alerta(s) detectada(s)</h2>
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {data.alerts.map((a, i) => {
                const sev = SEVERITY[a.severity] || SEVERITY.low;
                const target = a.target_url || (a.catalog && CATALOG_ROUTES[a.catalog] ? `/admin/catalogos-rrhh/${CATALOG_ROUTES[a.catalog]}` : null);
                return (
                  <div key={a.id || i} className="flex items-start gap-2 text-xs py-2 border-b border-amber-100 last:border-0" data-testid={`alert-${a.id || a.type + '-' + i}`}>
                    <Badge className={`rounded-sm text-[9px] border ${sev.class} flex-shrink-0 mt-0.5`}>{sev.label}</Badge>
                    <span className="text-slate-700 flex-1 leading-relaxed">{a.message}</span>
                    {target && (
                      <button onClick={() => navigate(target)}
                        className="text-[10px] uppercase font-semibold tracking-wider bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-2.5 py-1 whitespace-nowrap flex-shrink-0"
                        data-testid={`resolve-${a.id || i}`}>
                        {a.cta_label || "Resolver"} →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Catalog cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {loading ? (
          <div className="col-span-full text-center py-8 text-slate-400 text-sm">Cargando…</div>
        ) : data?.catalogs?.map(c => {
          const Icon = ICON_MAP[c.slug] || ClipboardList;
          const route = CATALOG_ROUTES[c.slug];
          const target = route ? `/admin/catalogos-rrhh/${route}` : `/admin/centros`;
          return (
            <Card key={c.slug} className={`border-slate-200 rounded-sm hover:border-slate-400 transition cursor-pointer ${c.is_empty ? "border-amber-300 bg-amber-50/20" : ""}`}
                  onClick={() => navigate(target)} data-testid={`card-${c.slug}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Icon className="w-4 h-4 text-slate-600" />
                  <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900">{c.label}</h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Activos:</span>
                  <span className="font-semibold text-green-700">{c.active}</span>
                  {c.inactive > 0 && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">Inactivos:</span>
                      <span className="font-semibold text-slate-700">{c.inactive}</span>
                    </>
                  )}
                  {c.archived > 0 && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500">Arch.:</span>
                      <span className="font-semibold text-amber-700">{c.archived}</span>
                    </>
                  )}
                </div>
                {c.is_empty && (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[9px] rounded-sm">Vacío</Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
